import type { Theme } from '@mui/material/styles'

import { getPortTypeHue } from '@/features/resource/components/PortTypeChip'
import useTruncatedSvgText from '@/features/resource/hooks/useTruncatedSvgText'
import { MIN_SVG_FONT_SIZE, NODE_HEIGHT, NODE_WIDTH } from '@/features/resource/utils/portGraph'
import type { GroupedPort } from '@/features/resource/utils/portGroups'

/**
 * The suffix of the id clipping a node's text to the node. Port names are free text, so one long
 * enough to run out of its box and across the band is a state to handle rather than one to hope
 * against. Nodes are drawn in their own translated frame, so one clip path in node-local
 * coordinates serves every one of them.
 *
 * A suffix rather than a whole id: SVG ids share one document-wide namespace, so two graphs on a
 * page would both define it and every node in the second would clip against the first one's rect.
 * The graph prefixes it per instance.
 */
export const NODE_LABEL_CLIP_SUFFIX = 'node-label-clip'

const LABEL_X = 16
const LABEL_RIGHT_PADDING = 10

/**
 * The one string the BFF sends for "no error" — a sentinel in the response rather than an absence.
 * The Errors column prints it verbatim, and a dot lighting up for it would report a fault on every
 * port.
 */
const NO_ERROR = 'None'

/**
 * A node's hue, read through the same function that hues the chip so the two views cannot disagree
 * about what an output looks like. A null hue is the deliberate absence of a category rather than a
 * missing value, so it falls back to the disabled text colour and not to one more hue.
 */
export function portNodeColor(port: GroupedPort, theme: Theme): string {
  return getPortTypeHue(port) ?? theme.palette.text.disabled
}

/**
 * How an edge is inked. A crossing relation is heavier and in the text colour — notable, and
 * deliberately not a warning hue, since crossing controllers is permitted by the data model and is
 * marked only because it is the case the table handles worst.
 */
export function portEdgeColor(theme: Theme, isCrossController: boolean): string {
  return isCrossController ? theme.palette.text.primary : theme.palette.grey[500]
}

export type PortGraphNodeShapeProps = {
  port: GroupedPort
  theme: Theme
  /** The port's type as the table words it, already flattened to one line. */
  typeLabel: string
  /** What a port with no `ports`/`name` value is called, since the box cannot be left blank. */
  unnamedLabel: string
  /** Metering ports show their arithmetic relation here instead of a type. */
  secondaryLabel?: string
  /** The graph instance's own clip-path id, since SVG ids are shared across the whole document. */
  labelClipId: string
  /** False for a LOCAL resource, whose error fields froze rather than went quiet. */
  hasLiveDeviceErrors: boolean
}

/**
 * A port, drawn in its own local frame — the caller positions it.
 *
 * Kind and direction are carried by form as well as by hue: a spine at the top is an input, at the
 * middle an output, hollow means the direction is not set, and a full-height spine with square
 * corners is a metering port. Nothing depends on colour alone, so the distinction survives for a
 * reader who cannot separate the hues — and it is the shape that carries it rather than the theme,
 * since `getPortTypeHue` returns fixed values rather than palette slots.
 */
export default function PortGraphNodeShape({
  port,
  theme,
  typeLabel,
  unnamedLabel,
  secondaryLabel,
  labelClipId,
  hasLiveDeviceErrors,
}: Readonly<PortGraphNodeShapeProps>) {
  const fullName = port.name ?? unnamedLabel

  // Measured after layout, since only a rendered `<text>` can say how wide a string is. In jsdom the
  // name stays whole and the clip path is what keeps it inside the node.
  const { ref: nameRef, displayText: displayName, isTruncated } = useTruncatedSvgText(fullName, NODE_LABEL_WIDTH)

  const color = portNodeColor(port, theme)
  const isMetering = port.kind === 'METERING'
  const direction = port.kind === 'CONTROL' ? (port.controlPort.isOutput ?? null) : null
  // A LOCAL resource's error fields stopped being written when its controller left monolith polling,
  // so a dot here would report a fault that may have been fixed months ago.
  const hasError = hasLiveDeviceErrors && port.errors !== null && port.errors !== NO_ERROR

  return (
    /*
     * One group around everything the node draws, so the `<title>` has the whole node area under it:
     * a browser resolves a tooltip by walking up to the nearest ancestor with a `<title>` child, and
     * on the label group alone only the glyph strip was hoverable.
     *
     * This group and not the node itself, because on an element carrying `role="button"` a `<title>`
     * falls through to its accessible *description* and the port name was read a second time after
     * the whole sentence. Here nothing announces it: the button ancestor makes descendants
     * presentational.
     */
    <g>
      {/*
       * Only when something was cut, so a tooltip means "there is more here". `<title>` rather than
       * a component tooltip leaves the node's handlers, ref and id alone, which the focus handling
       * depends on. First child, which is where SVG looks for it.
       */}
      {isTruncated && <title>{fullName}</title>}

      <rect
        fill={theme.palette.background.paper}
        height={NODE_HEIGHT}
        rx={isMetering ? 4 : 7}
        stroke={color}
        strokeWidth={isMetering ? 1.6 : 1.1}
        width={NODE_WIDTH}
        x={0}
        y={0}
      />

      {isMetering && <rect fill={color} height={NODE_HEIGHT - 2} rx={1.75} width={3.5} x={1} y={1} />}

      {/* Hollow, in the input slot: a direction belongs there and is not set, which should read as
          a gap rather than as a third direction. */}
      {!isMetering && direction === null && (
        <rect fill="none" height={17} rx={1.6} stroke={color} strokeWidth={1.1} width={3.2} x={1.6} y={8.6} />
      )}

      {!isMetering && direction !== null && (
        <rect fill={color} height={18} rx={1.75} width={3.5} x={1} y={direction ? NODE_HEIGHT / 2 : 8} />
      )}

      <g clipPath={`url(#${labelClipId})`}>
        <text
          ref={nameRef}
          fill={theme.palette.text.primary}
          fillOpacity={port.name === null ? 0.6 : 1}
          fontSize={13}
          fontStyle={port.name === null ? 'italic' : undefined}
          fontWeight={600}
          x={LABEL_X}
          y={22}
        >
          {displayName}
        </text>
        <text
          fill={theme.palette.text.secondary}
          // The floor this view puts under its smallest text; the reasoning is on the constant.
          fontSize={MIN_SVG_FONT_SIZE}
          letterSpacing="0.04em"
          style={{ textTransform: 'uppercase' }}
          x={LABEL_X}
          y={39}
        >
          {secondaryLabel ?? typeLabel}
        </text>
      </g>

      {/* Dormant on every port the seeded responses carry, which is the honest picture: the graph
          can say "something is wrong here" and nothing more, so the free text stays in the table. */}
      {hasError && <circle cx={NODE_WIDTH - 12} cy={NODE_HEIGHT - 12} fill={theme.palette.error.main} r={3.5} />}
    </g>
  )
}

/** The width a label has before the clip path cuts it, and what the truncation measures against. */
export const NODE_LABEL_WIDTH = NODE_WIDTH - LABEL_X - LABEL_RIGHT_PADDING

/** The rect the clip path above is built from, so the two cannot drift apart. */
export const NODE_LABEL_CLIP_RECT = {
  x: LABEL_X,
  y: 0,
  width: NODE_LABEL_WIDTH,
  height: NODE_HEIGHT,
}
